import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { addDays, format } from 'date-fns';
import { Subscription } from 'rxjs';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { FormControlService } from '~/core/services/form/form-control.service';
import { ParamethorService } from '~/paramethor/paramethor.service';

export interface DateRangeFilterInterface {
  from: Date;
  to: Date | null;
}

const DATE_FORMAT = 'yyyy-MM-dd';
@Component({
  selector: 'app-attendee-date-filter',
  templateUrl: './date-filter.component.html',
})
export class AttendeeDateFilterComponent implements OnInit, OnDestroy {
  @Input() public defaultValue?: DateRangeFilterInterface;
  @Output() public selected = new EventEmitter<DateRangeFilterInterface>();

  // styling properties
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;

  public dateFilterForm: FormGroup;

  // current component values
  public dateRange: DateRangeFilterInterface = null;
  public subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: FormBuilder,
    public readonly formControlService: FormControlService,
    public readonly translateService: TranslateService,
    public readonly paramethorService: ParamethorService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  public ngOnInit(): void {
    this.dateFilterForm = this.formBuilder.group({
      from: [this.defaultValue?.from, Validators.required],
      to: [this.defaultValue?.to],
    });
    this.dateRange = this.defaultValue;

    // set component value and close modal on emit
    this.subscriptions.add(
      this.selected.subscribe((value) => {
        this.dateRange = value;
      })
    );
  }

  // open cta icon
  public get buttonRightIcon(): string {
    if (this.dateRange) {
      return '';
    }

    return VtmnPlayIconTypeEnum.ArrowDown;
  }

  public get buttonVariant(): VtmnButtonVariantEnum {
    if (!this.dateRange) {
      return VtmnButtonVariantEnum.SECONDARY;
    }

    return VtmnButtonVariantEnum.PRIMARY;
  }

  public get filterLabel(): string {
    if (this.dateRange) {
      const label = `${format(this.dateRange.from, 'dd-MM-yyyy')}`;

      if (this.dateRange.to) {
        return label?.concat(
          `- ${format(addDays(this.dateRange.to, -1), 'dd-MM-yyyy')}`
        );
      }
      return label;
    }
    return this.translateService.instant('attendee.filters.date.label');
  }

  // from control accessor
  public get from(): AbstractControl {
    return this.dateFilterForm?.get('from');
  }

  // get minimum calendar date for from
  public get fromMinDate(): string {
    return format(new Date(), DATE_FORMAT);
  }

  public get hasDateRange(): boolean {
    return !!this.dateRange;
  }

  public get isFormValid(): boolean {
    return this.dateFilterForm.valid;
  }

  // to control accessor
  public get to(): AbstractControl {
    return this.dateFilterForm.get('to');
  }

  // get minimum calendar date for to ( = from value)
  public get toMinDate(): string {
    return this.from.value || this.fromMinDate;
  }

  /**
   * reset form
   */
  public clear(): void {
    this.dateFilterForm.reset();
    this.selected.emit(undefined);
  }

  // format error message
  public errorMessages(inputName: string): string[] {
    const control = this.dateFilterForm.get(inputName);

    return (
      control?.touched &&
      this.formControlService.getErrorMessages(control, inputName)
    );
  }

  // notify parent with new values
  public onSubmit(): void {
    if (!this.isFormValid) {
      return;
    }

    this.selected.emit({
      from: new Date(this.from.value),
      to: this.to?.value ? addDays(new Date(this.to.value), 1) : null,
    });
  }
}
